import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useTutorialStatus from '../hooks/useTutorialStatus';
import NewFeatureAnnouncementModal from './NewFeatureAnnouncementModal';

// Local storage key of the initial welcome walk-through
// (OnboardingTutorial.jsx). We must NOT overlap with it — new registrants
// see the initial tutorial first, then the schedina modal on the next
// app open.
const INITIAL_ONBOARDING_KEY = 'rvincente_onboarding_v1_seen';
// Grace latch: the first time this device is EVER eligible for the
// schedina modal, we skip it and set this flag. On the next app open
// (next mount with this flag already set) we show the modal for real.
// This gives freshly-registered users a clean run of the initial
// tutorial without our announcement layering on top.
const GRACE_LATCH_KEY = 'schedina_gate_grace_done';

/**
 * iter137 — SchedinaAnnouncementGate
 *
 * Mounts globally in the app. On every mount (each app open / route
 * change while logged-in) it:
 *   1. Waits for the user to be authenticated.
 *   2. Waits for the initial onboarding tutorial to be completed
 *      (`rvincente_onboarding_v1_seen`) — no stacking of tutorials.
 *   3. Skips the modal on the FIRST eligible mount ever (grace latch)
 *      so freshly-registered users only see it from the next app open.
 *   4. Fetches the `schedina_analyzer` tutorial flag.
 *   5. If NOT seen and we're NOT already on the tour deep-link
 *      (`/ia?tour=schedina`), shows a blocking modal:
 *        • "Prova subito" → navigate to /ia?tour=schedina (starts tour)
 *        • "Più tardi"    → close for this session, no server write
 *          (so it re-appears on the next login until the tour completes)
 *
 * Once the coach-mark tour completes it calls `markTourSeen()` and the
 * modal never appears again.
 */
export default function SchedinaAnnouncementGate() {
  const { user, loading: authLoading } = useAuth();
  const { seen, loading } = useTutorialStatus('schedina_analyzer');
  // The newer "La mia selezione" announcement goes first; this one waits until that tour is done.
  const { seen: mySelectionSeen } = useTutorialStatus('my_selection');
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  // Snapshot the initial-onboarding flag ONCE at mount so React re-renders
  // triggered by the tutorial completing (setting the flag mid-session)
  // don't immediately race the modal in front of the same user.
  const [initialOnboardingDone] = useState(() => {
    try { return !!localStorage.getItem(INITIAL_ONBOARDING_KEY); }
    catch { return true; }
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Reset the "dismissed this session" flag when the user logs out /
  // switches account, so the next login triggers it again.
  useEffect(() => {
    if (!user) setDismissedThisSession(false);
  }, [user]);

  if (authLoading || loading) return null;
  if (!user) return null;                                // guests never see it
  if (seen) return null;                                 // already viewed the tour
  if (!mySelectionSeen) return null;
  if (dismissedThisSession) return null;                 // "Più tardi" clicked
  // Never show the modal while the user is already ON the tour deep link,
  // otherwise it would immediately block the coach-mark overlay.
  if (location.pathname === '/ia' && /tour=schedina/.test(location.search)) return null;

  // Anti-overlap rule: freshly-registered users hit the standard welcome
  // walk-through first. Only announce the analyzer AFTER that tutorial
  // is complete — and even then wait one more app open, so the two
  // never sit on top of each other in the same session.
  if (!initialOnboardingDone) return null;

  try {
    if (!localStorage.getItem(GRACE_LATCH_KEY)) {
      localStorage.setItem(GRACE_LATCH_KEY, '1');
      return null;   // first eligible session → defer to the next one
    }
  } catch { /* private-mode: fall through and just show it */ }

  const goTour = () => {
    setDismissedThisSession(true);   // stop showing during this navigation
    navigate('/ia?tab=analyze&tour=schedina');
  };
  const later = () => setDismissedThisSession(true);

  return <NewFeatureAnnouncementModal onTryNow={goTour} onLater={later} />;
}
